/**
 * Secret-share Mux viewer — /reels/:token
 *
 * Anti-unfurl:
 *  1) Mount → PEEK only (no view burn) → gate UI with thumbs
 *  2) Human taps "Ver ahora" → open=1 → consume view + player
 *  3) sessionStorage caches open result so re-renders don't re-burn
 */
import ReelsFeed, { type ReelsWatchProgress } from "@/components/ReelsFeed";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_SITE_URL, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { Flame, Loader2, Link2Off, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

type Preview = { title: string; thumbnail_url: string };

type ViewState =
  | { kind: "loading" }
  | { kind: "error"; reason: string }
  | {
      kind: "gate";
      invitationId: string;
      previews: Preview[];
      maxViews: number;
      viewsCount: number;
      label: string | null;
      customMessage: string | null;
      snapshotCount: number;
    }
  | {
      kind: "ok";
      invitationId: string;
      reels: string[];
      burned: boolean;
      maxViews: number;
      viewsCount: number;
      label: string | null;
      customMessage: string | null;
    };

const MUX_DATA_ENV =
  (import.meta.env.VITE_MUX_DATA_ENV_KEY as string | undefined)?.trim() ||
  (import.meta.env.VITE_MUX_ENV_KEY as string | undefined)?.trim() ||
  "";

const OPEN_CACHE_PREFIX = "reel_secret_open_v1:";

function openCacheKey(token: string) {
  return `${OPEN_CACHE_PREFIX}${token}`;
}

type OpenCachePayload = {
  invitationId: string;
  reels: string[];
  burned: boolean;
  maxViews: number;
  viewsCount: number;
  label: string | null;
  customMessage: string | null;
  at: number;
};

/** Session grant after human open — short TTL; always revalidate with peek. */
const OPEN_CACHE_MAX_MS = 45 * 60_000; // 45 min

function readOpenCache(token: string): OpenCachePayload | null {
  try {
    const raw = sessionStorage.getItem(openCacheKey(token));
    if (!raw) return null;
    const j = JSON.parse(raw) as OpenCachePayload;
    if (!j || !Array.isArray(j.reels) || j.reels.length === 0) return null;
    if (Date.now() - (j.at || 0) > OPEN_CACHE_MAX_MS) {
      sessionStorage.removeItem(openCacheKey(token));
      return null;
    }
    return j;
  } catch {
    return null;
  }
}

function clearOpenCache(token: string) {
  try {
    sessionStorage.removeItem(openCacheKey(token));
  } catch {
    /* ignore */
  }
}

function writeOpenCache(token: string, payload: Omit<OpenCachePayload, "at">) {
  try {
    sessionStorage.setItem(
      openCacheKey(token),
      JSON.stringify({ ...payload, at: Date.now() }),
    );
  } catch {
    /* private mode / quota */
  }
}

function InvalidScreen({ reason }: { reason: string }) {
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center px-6">
      <div className="max-w-sm text-center space-y-4">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/5">
          <Link2Off className="h-8 w-8 text-white/50" />
        </div>
        <h1 className="text-xl font-medium text-white">Link desaparecido</h1>
        <p className="text-sm text-white/50 leading-relaxed">
          Este secret share de video ya no está disponible. Puede haberse
          consumido (vista usada), expirado por tiempo, o desactivado.
        </p>
        <p className="text-[10px] font-mono text-white/25">{reason}</p>
        <Link
          to="/"
          className="inline-block text-sm text-white/40 hover:text-white/70"
        >
          ← Volver al inicio
        </Link>
      </div>
    </div>
  );
}

async function postWatchTrack(body: Record<string, unknown>) {
  try {
    await fetch("/api/reel-invitations/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    /* best-effort */
  }
}

export default function ReelInviteViewer() {
  const { token: tokenParam } = useParams<{ token: string }>();
  const token = (tokenParam || "").trim().toLowerCase();
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [opening, setOpening] = useState(false);
  const [openHint, setOpenHint] = useState<string | null>(null);
  const openTrackedRef = useRef(false);
  const lastHeartbeatRef = useRef(0);

  useSEO({
    title: "Video privado",
    description: "Contenido compartido",
    robots: "noindex, nofollow",
    ogImage: `${BRAND_SITE_URL}/og-reels.jpg`,
  });

  // Mount: peek first (never auto open=1). Revalidate cache against peek.
  useEffect(() => {
    let cancelled = false;
    if (!/^[a-f0-9]{32}$/.test(token)) {
      setState({ kind: "error", reason: "not_found" });
      return;
    }

    void (async () => {
      try {
        const res = await fetch(
          `/api/reel-invitations/view?token=${encodeURIComponent(token)}`,
          { cache: "no-store" },
        );
        const data = (await res.json()) as {
          ok?: boolean;
          valid?: boolean;
          peek?: boolean;
          requires_open?: boolean;
          reason?: string;
          reels?: string[];
          previews?: Preview[];
          invitation?: {
            id?: string;
            max_views?: number;
            views_count?: number;
            label?: string | null;
            custom_message?: string | null;
            snapshot_count?: number;
            status?: string;
          };
        };

        if (cancelled) return;

        if (!res.ok || !data.valid) {
          clearOpenCache(token);
          setState({
            kind: "error",
            reason: data.reason || "not_found",
          });
          void mirrorFunnelEvent("reel_secret_share_denied", {
            reason: data.reason || "not_found",
            token_prefix: token.slice(0, 8),
            path: `/reels/${token}`,
            phase: "peek",
          });
          return;
        }

        // Invite still active: if we have a same-session open cache, reuse player
        // (does not re-burn). Dropped if peek says consumed/expired.
        const cached = readOpenCache(token);
        const status = data.invitation?.status;
        if (
          cached &&
          status === "active" &&
          (data.invitation?.views_count ?? 0) > 0
        ) {
          setState({
            kind: "ok",
            invitationId: cached.invitationId,
            reels: cached.reels,
            burned: cached.burned,
            maxViews: cached.maxViews,
            viewsCount: data.invitation?.views_count ?? cached.viewsCount,
            label: cached.label,
            customMessage: cached.customMessage,
          });
          return;
        }
        if (cached && status && status !== "active") {
          clearOpenCache(token);
        }

        // Peek gate — no playable reels
        setState({
          kind: "gate",
          invitationId: data.invitation?.id || "",
          previews: Array.isArray(data.previews) ? data.previews : [],
          maxViews: data.invitation?.max_views ?? 1,
          viewsCount: data.invitation?.views_count ?? 0,
          label: data.invitation?.label ?? null,
          customMessage: data.invitation?.custom_message ?? null,
          snapshotCount:
            data.invitation?.snapshot_count ??
            (Array.isArray(data.previews) ? data.previews.length : 0),
        });
      } catch {
        if (!cancelled) setState({ kind: "error", reason: "not_found" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const handleOpen = useCallback(async () => {
    if (opening) return;
    setOpening(true);
    try {
      const res = await fetch(
        `/api/reel-invitations/view?token=${encodeURIComponent(token)}&open=1`,
        { cache: "no-store" },
      );
      const data = (await res.json()) as {
        ok?: boolean;
        valid?: boolean;
        burned?: boolean;
        peek?: boolean;
        forced_peek?: boolean;
        requires_open?: boolean;
        reason?: string;
        reels?: string[];
        previews?: Preview[];
        invitation?: {
          id?: string;
          max_views?: number;
          views_count?: number;
          label?: string | null;
          custom_message?: string | null;
          snapshot_count?: number;
        };
      };

      if (!res.ok || !data.valid) {
        clearOpenCache(token);
        setState({ kind: "error", reason: data.reason || "not_found" });
        void mirrorFunnelEvent("reel_secret_share_denied", {
          reason: data.reason || "not_found",
          token_prefix: token.slice(0, 8),
          path: `/reels/${token}`,
          phase: "open",
        });
        return;
      }

      // Server forced peek (crawler UA) — stay on gate, do not show "dead link"
      if (data.peek || data.forced_peek) {
        setState({
          kind: "gate",
          invitationId: data.invitation?.id || "",
          previews: Array.isArray(data.previews) ? data.previews : [],
          maxViews: data.invitation?.max_views ?? 1,
          viewsCount: data.invitation?.views_count ?? 0,
          label: data.invitation?.label ?? null,
          customMessage: data.invitation?.custom_message ?? null,
          snapshotCount:
            data.invitation?.snapshot_count ??
            (Array.isArray(data.previews) ? data.previews.length : 0),
        });
        setOpenHint(
          "Abrí este link en el navegador (Safari/Chrome) para ver el video. Algunas apps en in-app no permiten la vista.",
        );
        return;
      }

      const reels = Array.isArray(data.reels) ? data.reels : [];
      if (reels.length === 0) {
        setState({ kind: "error", reason: "not_found" });
        return;
      }

      const invitationId = data.invitation?.id || "";
      const payload = {
        invitationId,
        reels,
        burned: Boolean(data.burned),
        maxViews: data.invitation?.max_views ?? 1,
        viewsCount: data.invitation?.views_count ?? 1,
        label: data.invitation?.label ?? null,
        customMessage: data.invitation?.custom_message ?? null,
      };
      writeOpenCache(token, payload);
      setState({ kind: "ok", ...payload });

      if (!openTrackedRef.current) {
        openTrackedRef.current = true;
        void mirrorFunnelEvent("reel_secret_share_open", {
          invitation_id: invitationId,
          token_prefix: token.slice(0, 8),
          label: data.invitation?.label ?? null,
          views_count: data.invitation?.views_count ?? 1,
          max_views: data.invitation?.max_views ?? 1,
          burned: Boolean(data.burned),
          reels_count: reels.length,
          path: `/reels/${token}`,
          access: "secret_share",
        });
      }
    } catch {
      setState({ kind: "error", reason: "not_found" });
    } finally {
      setOpening(false);
    }
  }, [opening, token]);

  const onWatchProgress = useCallback(
    (p: ReelsWatchProgress) => {
      if (state.kind !== "ok") return;
      const now = Date.now();
      const isMilestone =
        p.completed ||
        [10, 25, 50, 75, 90, 95, 100].includes(Math.floor(p.progressPct));

      if (!isMilestone && now - lastHeartbeatRef.current < 7000) return;
      lastHeartbeatRef.current = now;

      const event = p.completed
        ? "complete"
        : isMilestone
          ? "progress"
          : "heartbeat";

      void postWatchTrack({
        token,
        event,
        playbackId: p.playbackId,
        progressPct: p.progressPct,
        watchedSeconds: p.watchedSeconds,
        deltaSeconds: p.deltaSeconds,
        videoIndex: p.index,
        totalVideos: p.totalVideos,
      });

      if (isMilestone || p.completed) {
        void mirrorFunnelEvent(
          p.completed
            ? "reel_secret_share_complete"
            : "reel_secret_share_progress",
          {
            invitation_id: state.invitationId,
            token_prefix: token.slice(0, 8),
            label: state.label,
            playback_id: p.playbackId,
            progress_pct: p.progressPct,
            watched_seconds: p.watchedSeconds,
            video_index: p.index,
            total_videos: p.totalVideos,
            path: `/reels/${token}`,
            access: "secret_share",
          },
        );
      }
    },
    [state, token],
  );

  const onVideoChange = useCallback(
    (index: number, playbackId: string) => {
      if (state.kind !== "ok") return;
      void postWatchTrack({
        token,
        event: "video_change",
        playbackId,
        progressPct: 0,
        watchedSeconds: 0,
        deltaSeconds: 0,
        videoIndex: index,
        totalVideos: state.reels.length,
      });
    },
    [state, token],
  );

  if (state.kind === "loading") {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-white/50">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (state.kind === "error") {
    return <InvalidScreen reason={state.reason} />;
  }

  // ── Gate (peek) ────────────────────────────────────────────────────────
  if (state.kind === "gate") {
    const hero = state.previews[0];
    const more = state.previews.slice(1, 4);
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center px-5">
        <div className="w-full max-w-sm space-y-5">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 shadow-2xl shadow-violet-900/20">
            {hero ? (
              <img
                src={hero.thumbnail_url}
                alt=""
                className="aspect-[9/16] w-full object-cover max-h-[52vh]"
              />
            ) : (
              <div className="aspect-[9/16] max-h-[52vh] w-full bg-gradient-to-b from-violet-950/80 to-black flex items-center justify-center">
                <Flame className="h-10 w-10 text-amber-400/80" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 space-y-1">
              {state.label && (
                <p className="text-sm font-medium text-white">{state.label}</p>
              )}
              <p className="text-[11px] text-white/55">
                {state.snapshotCount || state.previews.length || "?"} video
                {(state.snapshotCount || state.previews.length) === 1
                  ? ""
                  : "s"}{" "}
                ·{" "}
                {state.maxViews === 1
                  ? "1 vista"
                  : state.maxViews >= 1_000_000
                    ? "vistas ilimitadas (con TTL)"
                    : `hasta ${state.maxViews} vistas`}
              </p>
            </div>
          </div>

          {more.length > 0 && (
            <div className="flex gap-2 justify-center">
              {more.map((p, i) => (
                <img
                  key={i}
                  src={p.thumbnail_url}
                  alt=""
                  className="h-14 w-10 rounded-lg object-cover ring-1 ring-white/10"
                />
              ))}
            </div>
          )}

          {state.customMessage && (
            <p className="text-center text-[12px] text-white/60 leading-relaxed">
              {state.customMessage}
            </p>
          )}

          <button
            type="button"
            onClick={() => void handleOpen()}
            disabled={opening}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3.5 text-sm font-semibold text-black transition hover:bg-white/90 disabled:opacity-60"
          >
            {opening ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4 fill-current" />
            )}
            Ver ahora
          </button>

          {openHint && (
            <p className="text-center text-[11px] text-amber-200/90 leading-relaxed px-2">
              {openHint}
            </p>
          )}

          <p className="text-center text-[10px] text-white/35 leading-relaxed px-2">
            Las previews de WhatsApp no gastan la vista. Solo se consume al
            tocar «Ver ahora».
          </p>
        </div>
      </div>
    );
  }

  // ── Player (after open) ────────────────────────────────────────────────
  const showBurnBanner = state.burned || state.maxViews === 1;
  const showCounter = state.maxViews > 1 && state.maxViews < 1_000_000;

  return (
    <div className="relative">
      {(showBurnBanner || showCounter || state.customMessage) && (
        <div className="pointer-events-none fixed top-0 left-0 right-0 z-[100] flex flex-col items-center gap-1 px-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          {showBurnBanner && (
            <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-amber-500/30 bg-black/75 px-3 py-1.5 text-[11px] text-amber-100/95 backdrop-blur-md">
              <Flame className="h-3.5 w-3.5 text-amber-400" />
              {state.burned
                ? "Última vista consumida — este dispositivo puede seguir viendo un rato; otros dispositivos ya no"
                : "Vista única — al salir, otros dispositivos no podrán abrir este link"}
            </div>
          )}
          {showCounter && (
            <div className="rounded-full border border-white/10 bg-black/60 px-3 py-1 text-[10px] text-white/70 backdrop-blur">
              Vista {state.viewsCount} / {state.maxViews}
            </div>
          )}
          {state.customMessage && (
            <p className="max-w-sm text-center text-[11px] text-white/55">
              {state.customMessage}
            </p>
          )}
        </div>
      )}

      <ReelsFeed
        initialPlaybackIds={state.reels}
        emptyMessage="Este share no tiene videos"
        onWatchProgress={onWatchProgress}
        onVideoChange={onVideoChange}
        muxDataEnvKey={MUX_DATA_ENV || undefined}
        muxDataMetadata={{
          viewer_user_id: state.invitationId || token.slice(0, 16),
          sub_property_id: BRAND_SOURCE_KEY,
          player_name: "secret-share-reels",
          video_series: state.label || "secret_share",
          experiment_name: "secret_share",
        }}
      />
    </div>
  );
}
